import { NOT_LOGGED_IN_USER_ID } from '@ori/auth';
import {
  useApiClient,
  type ApiClient,
  type GlobalCustomersDocumentApprovalDto,
  getCustomerDocumentsInfos,
  getCustomerDocumentApprovals,
} from '@ori/global-api-client-react';
import type { AxiosRequestHeaders } from 'axios';
import useSWR from 'swr';

import { useNotificationData } from '../../contexts';
import { useCustomHeaders } from '../../hooks';
import { useTranslations } from '../../hooks/useTranslations_simplified-registration-BrandPartner';
import type { IDocumentsInfo2 } from '../../interfaces';
import { MessageType } from '../../interfaces';
import { FetchResult } from '../FetchResult';
import { TranslationKey } from '../TranslationKey_simplified-registration-BrandPartner';
import { useRegistrationData } from './registrations';

async function fetchCustomerDocumentInfo(api: ApiClient, customerId: number, customHeaders?: AxiosRequestHeaders) {
  const emptyCustomerDocumentApprovals: GlobalCustomersDocumentApprovalDto[] = [];
  try {
    const [documents, approvals] = await Promise.all([
      getCustomerDocumentsInfos(api, customerId, {}, customHeaders),
      getCustomerDocumentApprovals(api, customerId, {}, customHeaders),
    ]);

    const isSuccess = FetchResult.isSuccess(documents.status, approvals.status);

    if (isSuccess) {
      const data = documents.data.map((info) => {
        const collection = approvals.data.collection.length > 0 ? approvals.data.collection : [];
        const allApprovals = collection;
        const documentApproval = allApprovals.find((approval) => approval.documentIds.includes(info.documentId));

        return {
          ...info,
          approval: documentApproval,
        };
      });

      return data;
    }

    return undefined;
  } catch (error: unknown) {
    return FetchResult.errorFor(emptyCustomerDocumentApprovals);
  }
}

export const useCustomerDocumentsInfos = (reFetchkey?: string) => {
  const { data: registrationData } = useRegistrationData();
  const registrationCustomerId = registrationData?.customerId ?? NOT_LOGGED_IN_USER_ID;
  const headers = useCustomHeaders();

  const { formatMessage } = useTranslations();
  const api = useApiClient();

  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData } = notificationData;
  const errorMsg = formatMessage(TranslationKey.cannotGetData);

  return useSWR(
    registrationCustomerId && registrationCustomerId !== -1
      ? [registrationCustomerId, reFetchkey, 'documentInfo']
      : null,
    async () => {
      const result = await fetchCustomerDocumentInfo(api, registrationCustomerId, headers);
      if (result) {
        return result as IDocumentsInfo2[];
      }
      throw new Error('request failed fetch document approvals');
    },
    {
      onError(err?: FetchResult) {
        if (err) {
          setNotificationData({
            isOpen: true,
            message: errorMsg,
            type: MessageType.error,
          });
        }
      },
    },
  );
};
